import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Modal,
} from "@mui/material";
import { CalendarToday, Person, Phone, Add, Remove } from "@mui/icons-material";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const CheckAvailabilityCard = ({ stayName }) => {
  const [step, setStep] = useState(1);
  // Set default check-in to today, check-out to tomorrow
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(tomorrow);
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [checkInAnchor, setCheckInAnchor] = useState(null);
  const [checkOutAnchor, setCheckOutAnchor] = useState(null);
  const [showCheckInPicker, setShowCheckInPicker] = useState(false);
  const [showCheckOutPicker, setShowCheckOutPicker] = useState(false);

  const formatDate = (date) => {
    const options = { day: "numeric", month: "long" };
    return date.toLocaleDateString("en-US", options);
  };

  const handleContinue = () => {
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const incrementCount = (type) => {
    if (type === "adults") {
      setAdults((prev) => prev + 1);
    } else {
      setChildren((prev) => prev + 1);
    }
  };

  const decrementCount = (type) => {
    if (type === "adults" && adults > 1) {
      setAdults((prev) => prev - 1);
    } else if (type === "children" && children > 0) {
      setChildren((prev) => prev - 1);
    }
  };

  const handleCheckInClick = (event) => {
    setShowCheckInPicker(true);
  };

  const handleCheckOutClick = (event) => {
    setShowCheckOutPicker(true);
  };

  const handleCheckInClose = () => {
    setShowCheckInPicker(false);
  };

  const handleCheckOutClose = () => {
    setShowCheckOutPicker(false);
  };

  const renderStep1 = () => (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "Monument-Private",
          fontWeight: 700,
          mb: 3,
        }}
      >
        Check availability
      </Typography>

      {/* Check-in / Check-out */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            p: 2,
            border: "1px solid rgba(0, 0, 0, 0.2)",
            borderRadius: 2,
          }}
        >
          {/* Check In */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              cursor: "pointer",
            }}
            onClick={handleCheckInClick}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarToday
                sx={{ fontSize: "0.9rem", color: "text.secondary" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.2, fontSize: "0.75rem" }}
              >
                Check In
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {formatDate(checkIn)}
              </Typography>
            </Box>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              mx: 2,
            }}
          />

          {/* Check Out */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              cursor: "pointer",
            }}
            onClick={handleCheckOutClick}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarToday
                sx={{ fontSize: "0.9rem", color: "text.secondary" }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.2, fontSize: "0.75rem" }}
              >
                Check Out
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {formatDate(checkOut)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Check In Date Picker Modal */}
      <Modal
        open={showCheckInPicker}
        onClose={() => setShowCheckInPicker(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            outline: "none",
            borderRadius: 3,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              fontFamily: "Monument-Private",
            }}
          >
            Check in
          </Typography>
          <Box ml={-3} mr={-2}>
            <StaticDatePicker
              value={checkIn}
              slots={{
                toolbar: null,
              }}
              onChange={(newValue) => setCheckIn(newValue)}
              renderInput={() => null}
              componentsProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowCheckInPicker(false)}
              sx={{
                flex: 1,
                borderColor: "rgba(0, 0, 0, 0.2)",
                color: "text.primary",
                textTransform: "none",
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.4)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowCheckInPicker(false)}
              sx={{
                flex: 1,
                backgroundColor: "#000",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Select
            </Button>
          </Box>
        </Card>
      </Modal>

      {/* Check Out Date Picker Modal */}
      <Modal
        open={showCheckOutPicker}
        onClose={() => setShowCheckOutPicker(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            outline: "none",
            borderRadius: 3,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              fontFamily: "Monument-Private",
            }}
          >
            Check out
          </Typography>
<Box ml={-3} mr={-2}>
              <StaticDatePicker
            value={checkOut}
            onChange={(newValue) => setCheckOut(newValue)}
            renderInput={() => null}
            slots={{ toolbar: null }}
            componentsProps={{
              actionBar: {
                actions: [],
              },
            }}
          />
</Box>
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowCheckOutPicker(false)}
              sx={{
                flex: 1,
                borderColor: "rgba(0, 0, 0, 0.2)",
                color: "text.primary",
                textTransform: "none",
                "&:hover": {
                  borderColor: "rgba(0, 0, 0, 0.4)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowCheckOutPicker(false)}
              sx={{
                flex: 1,
                backgroundColor: "#000",
                color: "white",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Select
            </Button>
          </Box>
        </Card>
      </Modal>

      {/* Continue Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleContinue}
        sx={{
          backgroundColor: "#000",
          color: "white",
          py: 2,
          fontWeight: 600,
          fontSize: "1rem",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#333",
          },
        }}
      >
        Continue
      </Button>
    </LocalizationProvider>
  );

  const renderStep2 = () => (
    <>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "Monument-Private",
          fontWeight: 700,
          mb: 3,
        }}
      >
        Personal Details
      </Typography>

      {/* Full Name Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Phone Number Field */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Phone Number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            flex: 1,
            py: 2,
            borderColor: "rgba(0, 0, 0, 0.2)",
            color: "text.primary",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              borderColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{
            flex: 1,
            backgroundColor: "#000",
            color: "white",
            py: 2,
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#333",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const handleSendWhatsApp = () => {
    // WhatsApp message with proper encoding and optional stayName
    const message =
      `Booking Enquiry:` +
      (stayName ? `%0AStay: ${encodeURIComponent(stayName)}` : "") +
      `%0AName: ${encodeURIComponent(fullName)}` +
      `%0APhone: ${encodeURIComponent(phoneNumber)}` +
      `%0ACheck-in: ${encodeURIComponent(formatDate(checkIn))}` +
      `%0ACheck-out: ${encodeURIComponent(formatDate(checkOut))}` +
      `%0AAdults: ${adults}` +
      `%0AChildren: ${children}`;
    const whatsappUrl = `https://wa.me/919673244873?text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  const renderStep3 = () => (
    <>
      <Typography
        variant="h5"
        sx={{
          fontFamily: "Monument-Private",
          fontWeight: 700,
          mb: 3,
        }}
      >
        Guest Count
      </Typography>

      {/* Adults and Children */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            p: 2,
            border: "1px solid rgba(0, 0, 0, 0.2)",
            borderRadius: 2,
          }}
        >
          {/* Adults */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: 1,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.2, fontSize: "0.75rem" }}
              >
                Adults
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {adults} adult{adults !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => decrementCount("adults")}
                disabled={adults <= 1}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
              >
                <Remove sx={{ fontSize: "0.8rem" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => incrementCount("adults")}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <Add sx={{ fontSize: "0.8rem" }} />
              </IconButton>
            </Box>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: "1px",
              backgroundColor: "rgba(0, 0, 0, 0.1)",
              mx: 2,
            }}
          />

          {/* Children */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: 1,
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.2, fontSize: "0.75rem" }}
              >
                Children
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {children} child{children !== 1 ? "ren" : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => decrementCount("children")}
                disabled={children <= 0}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                  "&:disabled": {
                    backgroundColor: "rgba(0, 0, 0, 0.02)",
                  },
                }}
              >
                <Remove sx={{ fontSize: "0.8rem" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => incrementCount("children")}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <Add sx={{ fontSize: "0.8rem" }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Buttons */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{
            flex: 1,
            py: 2,
            borderColor: "rgba(0, 0, 0, 0.2)",
            color: "text.primary",
            textTransform: "none",
            fontWeight: 500,
            "&:hover": {
              borderColor: "rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSendWhatsApp}
          sx={{
            flex: 1,
            backgroundColor: "#000",
            color: "white",
            py: 2,
            fontWeight: 600,
            fontSize: "1rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#333",
            },
          }}
        >
          Send enquiry
        </Button>
      </Box>
    </>
  );

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(0, 0, 0, 0.1)",
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </CardContent>
    </Card>
  );
};

export default CheckAvailabilityCard;
